import { useRef, useState, type FormEvent } from 'react';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useTranslation } from 'react-i18next';
import { ErrorCodes, isApiError } from '@/api/errors';
import {
  envelopesApi,
  type PeriodKind,
  type EnvelopeCreateRequest,
  type EnvelopeKind,
  type EnvelopePatchRequest,
  type Envelope,
} from '@/api/envelopes';
import { DialogDoneButton } from '@/components/DialogDoneButton';
import { DialogSubtitle } from '@/components/DialogSubtitle';
import { applyBackendFields, validationAlert } from '@/features/shared/useFieldErrors';
import { SubscribeInPlace } from '@/features/subscribe/SubscribeInPlace';
import { useMe } from '@/session/meHooks';
import { bpToInputText, minorToInputText, parseMoneyToMinor, percentToBp } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { todayCivilInZone } from '@/i18n/zonedCivil';
import { AutoApplyThresholdField } from '@/features/automation/AutoApplyThresholdField';
import { GuardedDialog } from '@/components/GuardedDialog';
import { SubmitButton } from '@/components/SubmitButton';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { useDirty } from '@/shared/useDirty';
import { useDialogFullScreen } from '@/shell/useDialogFullScreen';
import { resolveTokens } from '@/theme/theme';

const TITLE_ID = 'envelope-form-title';
const SUBTITLE_ID = 'envelope-form-subtitle';
const MORE_ID = 'envelope-form-more';
const NAME_MAX_LENGTH = 120;
const NOTES_MAX_LENGTH = 2000;
const ROLLING_MIN = 1;
const ROLLING_MAX = 36;
const KINDS: EnvelopeKind[] = ['CASH', 'SAVINGS', 'BUDGET'];
const PERIOD_KINDS: PeriodKind[] = ['CALENDAR_YEAR', 'FISCAL_YEAR', 'ROLLING_MONTHS', 'ALL_TIME'];
const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] as const;

interface EnvelopeFormDialogProps {
  owner: boolean;
  virtualAccountId: number;
  /** The virtual account OWNER's currency — targets are entered and shown in it (§14.1). */
  currency: string | null;
  /** Present when editing; absent when creating. */
  envelope?: Envelope;
  /** The `sortOrder` a created envelope takes (after every existing one). */
  nextSortOrder: number;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}

interface FormState {
  name: string;
  kind: EnvelopeKind;
  target: string;
  targetOn: string;
  periodKind: PeriodKind;
  fiscalYearStartMonth: number;
  rollingMonths: string;
  apr: string;
  notes: string;
}

const ENVELOPE_FIELDS = [
  'name',
  'kind',
  'targetMinor',
  'targetOn',
  'periodKind',
  'fiscalYearStartMonth',
  'rollingMonths',
  'referenceAprBp',
  'notes',
] as const;

type EnvelopeApiField = (typeof ENVELOPE_FIELDS)[number];
type UiField =
  | 'name'
  | 'kind'
  | 'target'
  | 'targetOn'
  | 'periodKind'
  | 'fiscalYearStartMonth'
  | 'rollingMonths'
  | 'apr'
  | 'notes';
type FieldErrors = Partial<Record<UiField, string>>;

/** The D1 fields "More settings" holds; an error on one opens it. */
const MORE_FIELDS: UiField[] = ['targetOn', 'periodKind', 'fiscalYearStartMonth', 'rollingMonths'];

function toUiField(field: EnvelopeApiField): UiField {
  if (field === 'targetMinor') return 'target';
  if (field === 'referenceAprBp') return 'apr';
  return field;
}

function initialState(
  envelope: Envelope | undefined,
  currency: string | null,
  locale: string,
): FormState {
  return {
    name: envelope?.name ?? '',
    kind: envelope?.kind ?? 'CASH',
    target:
      envelope?.targetMinor != null ? minorToInputText(envelope.targetMinor, currency, locale) : '',
    targetOn: envelope?.targetOn ?? '',
    periodKind: envelope?.periodDefault.periodKind ?? 'CALENDAR_YEAR',
    fiscalYearStartMonth: envelope?.periodDefault.fiscalYearStartMonth ?? 1,
    rollingMonths:
      envelope?.periodDefault.rollingMonths != null
        ? String(envelope.periodDefault.rollingMonths)
        : '12',
    apr: envelope?.referenceAprBp != null ? bpToInputText(envelope.referenceAprBp, locale) : '',
    notes: envelope?.notes ?? '',
  };
}

/**
 * Create / edit an envelope (design §7.6–§7.7, review A3; restyled to C5 by TMMAPP-0108, audit 06
 * § C5, § Overrides 6, v2 L1962–2011): "New envelope" / "Edit envelope" over "In {VA}", the type cards
 * Cash / Savings / Budget with their notes, the type note, the dashed tip, Name, then a Savings
 * envelope's Goal and APR ("Reference only") or a Budget envelope's budget amount and Notes; the D1
 * fields (period, goal date, auto-apply) under "More settings"; CTA Create / Save. A patch sends only
 * the changed fields; a cleared optional one is sent as `null`.
 */
export function EnvelopeFormDialog({
  owner,
  virtualAccountId,
  currency,
  envelope,
  nextSortOrder,
  onClose,
  onSaved,
}: EnvelopeFormDialogProps) {
  const { t } = useTranslation();
  const me = useMe();
  const timeZone = me.user.timezone ?? 'UTC';
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const fullScreen = useDialogFullScreen();
  const virtualAccountName =
    me.virtualAccounts.find((row) => row.id === virtualAccountId)?.name ?? '';
  const [aiAutoApplyBp, setAiAutoApplyBp] = useState(envelope?.aiAutoApplyBp ?? null);
  const [form, setForm] = useState<FormState>(() => initialState(envelope, currency, locale));
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [submitAlert, setSubmitAlert] = useState<string | null>(null);
  const [subscribeRequired, setSubscribeRequired] = useState(false);
  const [more, setMore] = useState(false);
  const { dirty } = useDirty({ form, aiAutoApplyBp });
  const guard = useDialogDirtyGuard(dirty, onClose, submitting);
  // The name field takes focus once the dialog has entered (`autoFocus` is a lint error here).
  const nameRef = useRef<HTMLInputElement>(null);

  const editing = envelope !== undefined;
  const kindChanged = editing && form.kind !== envelope.kind;
  const showsTarget = form.kind !== 'CASH';
  const savings = form.kind === 'SAVINGS';
  const budget = form.kind === 'BUDGET';
  const trimmedName = form.name.trim();
  const showMore = more || MORE_FIELDS.some((field) => fieldErrors[field] !== undefined);
  const label = tokens.textLabel('surfaceRaised');
  const monthName = (month: number) =>
    new Intl.DateTimeFormat(locale, { month: 'long' }).format(new Date(2026, month - 1, 1));

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  function handleKind(kind: EnvelopeKind) {
    if (kind === form.kind) return;
    // Changing the kind clears the target fields (they mean something else under the new kind).
    setForm((prev) => ({ ...prev, kind, target: '', targetOn: '', apr: '' }));
  }

  /** The validated request fields the form currently describes (`null` on a field error). */
  function collect(): {
    create: EnvelopeCreateRequest;
    targetMinor: number | null;
    aprBp: number | null;
  } | null {
    const errors: FieldErrors = {};
    if (trimmedName.length === 0) errors.name = t('envelope.errorValidation');
    let targetMinor: number | null = null;
    if (showsTarget && form.target.trim().length > 0) {
      targetMinor = parseMoneyToMinor(form.target, currency, locale);
      if (targetMinor === null) errors.target = t('envelope.errorValidation');
    }
    let aprBp: number | null = null;
    if (savings && form.apr.trim().length > 0) {
      aprBp = percentToBp(form.apr.replace('%', '').trim(), locale);
      if (aprBp === null) errors.apr = t('envelope.errorValidation');
    }
    const rolling = Number(form.rollingMonths);
    if (
      form.periodKind === 'ROLLING_MONTHS' &&
      (!Number.isInteger(rolling) || rolling < ROLLING_MIN || rolling > ROLLING_MAX)
    ) {
      errors.rollingMonths = t('envelope.errorValidation');
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return null;

    const create: EnvelopeCreateRequest = {
      name: trimmedName,
      kind: form.kind,
      periodKind: form.periodKind,
    };
    if (targetMinor !== null) create.targetMinor = targetMinor;
    if (savings && form.targetOn) create.targetOn = form.targetOn;
    if (form.periodKind === 'FISCAL_YEAR') create.fiscalYearStartMonth = form.fiscalYearStartMonth;
    if (form.periodKind === 'ROLLING_MONTHS') create.rollingMonths = rolling;
    if (aprBp !== null) create.referenceAprBp = aprBp;
    if (budget && form.notes.trim().length > 0) create.notes = form.notes.trim();
    return { create, targetMinor, aprBp };
  }

  /**
   * Only the fields that differ from the loaded envelope (TMMAPP-0134, TMMAPI-0167). `aiAutoApplyBp`,
   * and since TMMAPI-0169 the target, its date and the APR, clear with `null` (TMMAPP-0136 item 11);
   * a budget cleared here is "no budget" from today (`targetEffectiveFrom`). A kind change clears
   * the target server-side, so nothing is sent for it then. Cleared notes send `''`.
   */
  function diff(current: Envelope, next: EnvelopeCreateRequest): EnvelopePatchRequest {
    const body: EnvelopePatchRequest = {};
    const sameKind = next.kind === current.kind;
    if (owner && aiAutoApplyBp !== current.aiAutoApplyBp) body.aiAutoApplyBp = aiAutoApplyBp;
    if (next.name !== current.name) body.name = next.name;
    if (!sameKind) body.kind = next.kind;
    if (next.targetMinor !== undefined && next.targetMinor !== current.targetMinor) {
      body.targetMinor = next.targetMinor;
    } else if (
      sameKind &&
      showsTarget &&
      next.targetMinor === undefined &&
      current.targetMinor !== null
    ) {
      body.targetMinor = null;
      if (next.kind === 'BUDGET') body.targetEffectiveFrom = todayCivilInZone(timeZone);
    }
    if (next.targetOn !== undefined && next.targetOn !== current.targetOn) {
      body.targetOn = next.targetOn;
    } else if (sameKind && savings && next.targetOn === undefined && current.targetOn !== null) {
      body.targetOn = null;
    }
    if (next.referenceAprBp !== undefined && next.referenceAprBp !== current.referenceAprBp) {
      body.referenceAprBp = next.referenceAprBp;
    } else if (
      sameKind &&
      savings &&
      next.referenceAprBp === undefined &&
      current.referenceAprBp !== null
    ) {
      body.referenceAprBp = null;
    }
    const notes = next.notes ?? '';
    if (notes !== (current.notes ?? '')) body.notes = notes;
    if (next.periodKind !== undefined && next.periodKind !== current.periodDefault.periodKind) {
      body.periodKind = next.periodKind;
    }
    if (
      next.fiscalYearStartMonth !== undefined &&
      next.fiscalYearStartMonth !== current.periodDefault.fiscalYearStartMonth
    ) {
      body.fiscalYearStartMonth = next.fiscalYearStartMonth;
    }
    if (
      next.rollingMonths !== undefined &&
      next.rollingMonths !== current.periodDefault.rollingMonths
    ) {
      body.rollingMonths = next.rollingMonths;
    }
    return body;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) return;
    const collected = collect();
    if (collected === null) {
      guard.focusErrors();
      return;
    }
    setSubmitting(true);
    setSubmitAlert(null);
    try {
      if (envelope) {
        const body = diff(envelope, collected.create);
        if (Object.keys(body).length > 0) await envelopesApi.update(envelope.id, body);
      } else {
        await envelopesApi.create(virtualAccountId, {
          ...collected.create,
          sortOrder: nextSortOrder,
        });
      }
      await onSaved();
      onClose();
    } catch (error) {
      const code = isApiError(error) ? error.code : ErrorCodes.UNKNOWN;
      if (code === ErrorCodes.VALIDATION_FAILED) {
        const next: FieldErrors = {};
        const result = applyBackendFields(
          error,
          ENVELOPE_FIELDS,
          (field, message) => {
            next[toUiField(field)] = message;
          },
          t,
          guard.focusErrors,
        );
        setFieldErrors((prev) => ({ ...prev, ...next }));
        setSubmitAlert(validationAlert(result, error, t, t('envelope.errorValidation')));
      } else if (code === ErrorCodes.SUBSCRIPTION_REQUIRED) setSubscribeRequired(true);
      else setSubmitAlert(t('virtualAccount.errorGeneric'));
      if (code !== ErrorCodes.SUBSCRIPTION_REQUIRED) guard.focusErrors();
      setSubmitting(false);
    }
  }

  return (
    <GuardedDialog
      guard={guard}
      titleId={TITLE_ID}
      subtitle={
        <DialogSubtitle id={SUBTITLE_ID}>
          {t('envelope.inVirtualAccount', { name: virtualAccountName })}
        </DialogSubtitle>
      }
      subtitleId={SUBTITLE_ID}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="sm"
      slotProps={{ transition: { onEntered: () => nameRef.current?.focus() } }}
    >
      <form onSubmit={(event) => void handleSubmit(event)} noValidate>
        <DialogTitle id={TITLE_ID}>
          {editing ? t('envelope.formEditTitle') : t('envelope.formCreateTitle')}
        </DialogTitle>
        <DialogContent>
          {subscribeRequired ? (
            <SubscribeInPlace onClose={onClose} />
          ) : (
            <Stack spacing={2} sx={{ mt: 2 }}>
              {submitAlert !== null && <Alert severity="error">{submitAlert}</Alert>}
              <Box>
                <Box
                  role="radiogroup"
                  aria-label={t('envelope.kindLabel')}
                  sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}
                >
                  {KINDS.map((kind) => (
                    <KindCard
                      key={kind}
                      kind={kind}
                      checked={form.kind === kind}
                      onSelect={() => handleKind(kind)}
                    />
                  ))}
                </Box>
                {fieldErrors.kind !== undefined && (
                  <FormHelperText error>{fieldErrors.kind}</FormHelperText>
                )}
                {kindChanged && fieldErrors.kind === undefined && (
                  <FormHelperText>{t('envelope.kindChangeClearsTarget')}</FormHelperText>
                )}
                <Typography
                  data-kind-note=""
                  sx={{ mt: '10px', fontSize: 12, lineHeight: 1.6, color: label }}
                >
                  {t(`envelope.kindNote.${form.kind}`, { name: virtualAccountName })}
                </Typography>
                <Box
                  data-envelope-tip=""
                  sx={{
                    mt: 1.5,
                    p: '10px 12px',
                    border: `1px dashed ${tokens.line}`,
                    borderRadius: `${tokens.radius.control}px`,
                    fontSize: 12,
                    lineHeight: 1.6,
                    color: tokens.text2,
                  }}
                >
                  {t('envelope.formTip')}
                </Box>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ gap: 1.5 }}>
                <TextField
                  label={t('envelope.nameLabel')}
                  placeholder={t('envelope.namePlaceholder')}
                  value={form.name}
                  onChange={(event) => set('name', event.target.value)}
                  inputRef={nameRef}
                  required
                  fullWidth
                  error={fieldErrors.name !== undefined}
                  helperText={fieldErrors.name}
                  slotProps={{ htmlInput: { maxLength: NAME_MAX_LENGTH } }}
                  sx={{ flex: '1 1 220px' }}
                />
                {savings && (
                  <TextField
                    label={t('envelope.goal')}
                    placeholder={t('envelope.goalPlaceholder')}
                    value={form.target}
                    onChange={(event) => set('target', event.target.value)}
                    error={fieldErrors.target !== undefined}
                    helperText={fieldErrors.target}
                    slotProps={{ htmlInput: { inputMode: 'decimal' } }}
                    sx={{ flex: '0 1 150px' }}
                  />
                )}
                {savings && (
                  <TextField
                    label={t('envelope.referenceApr')}
                    placeholder={t('envelope.aprPlaceholder')}
                    value={form.apr}
                    onChange={(event) => set('apr', event.target.value)}
                    error={fieldErrors.apr !== undefined}
                    helperText={fieldErrors.apr ?? t('envelope.referenceOnly')}
                    slotProps={{ htmlInput: { inputMode: 'decimal' } }}
                    sx={{ flex: '0 1 110px' }}
                  />
                )}
              </Stack>
              {budget && (
                <TextField
                  label={t('envelope.budgetAmount')}
                  value={form.target}
                  onChange={(event) => set('target', event.target.value)}
                  error={fieldErrors.target !== undefined}
                  helperText={fieldErrors.target ?? t('envelope.targetHelp')}
                  slotProps={{
                    htmlInput: { inputMode: 'decimal' },
                    input: {
                      startAdornment: <InputAdornment position="start">{currency}</InputAdornment>,
                    },
                  }}
                  sx={{ maxWidth: 260 }}
                />
              )}
              {budget && (
                <TextField
                  label={t('envelope.notes')}
                  placeholder={t('envelope.notesPlaceholder')}
                  value={form.notes}
                  onChange={(event) => set('notes', event.target.value)}
                  multiline
                  minRows={3}
                  fullWidth
                  error={fieldErrors.notes !== undefined}
                  helperText={fieldErrors.notes ?? t('envelope.notesHelp')}
                  slotProps={{ htmlInput: { maxLength: NOTES_MAX_LENGTH } }}
                />
              )}
              <Box>
                <Button
                  variant="text"
                  aria-expanded={showMore}
                  aria-controls={MORE_ID}
                  onClick={() => setMore((value) => !value)}
                  sx={{ px: 1, marginInlineStart: -1 }}
                >
                  {t('envelope.moreSettings')}
                </Button>
                {showMore && (
                  <Stack id={MORE_ID} spacing={2} sx={{ mt: 1 }}>
                    {editing && owner && (
                      <AutoApplyThresholdField
                        label={t('autoApply.categoryLabel')}
                        value={aiAutoApplyBp}
                        onChange={setAiAutoApplyBp}
                        automationKeys={['AI_CATEGORY_SUGGESTION']}
                        disabled={submitting}
                      />
                    )}
                    {savings && (
                      <TextField
                        label={t('envelope.targetOn')}
                        type="date"
                        value={form.targetOn}
                        onChange={(event) => set('targetOn', event.target.value)}
                        fullWidth
                        error={fieldErrors.targetOn !== undefined}
                        helperText={fieldErrors.targetOn}
                        slotProps={{ inputLabel: { shrink: true } }}
                      />
                    )}
                    <FormControl fullWidth error={fieldErrors.periodKind !== undefined}>
                      <InputLabel id="envelope-form-period">{t('envelope.periodLabel')}</InputLabel>
                      <Select
                        labelId="envelope-form-period"
                        label={t('envelope.periodLabel')}
                        value={form.periodKind}
                        onChange={(event) => set('periodKind', event.target.value as PeriodKind)}
                      >
                        {PERIOD_KINDS.map((kind) => (
                          <MenuItem key={kind} value={kind}>
                            {t(`envelope.periodKind.${kind}`)}
                          </MenuItem>
                        ))}
                      </Select>
                      {fieldErrors.periodKind !== undefined && (
                        <FormHelperText>{fieldErrors.periodKind}</FormHelperText>
                      )}
                    </FormControl>
                    {form.periodKind === 'FISCAL_YEAR' && (
                      <FormControl fullWidth error={fieldErrors.fiscalYearStartMonth !== undefined}>
                        <InputLabel id="envelope-form-fy">
                          {t('envelope.fiscalYearStartMonth')}
                        </InputLabel>
                        <Select
                          labelId="envelope-form-fy"
                          label={t('envelope.fiscalYearStartMonth')}
                          value={form.fiscalYearStartMonth}
                          onChange={(event) =>
                            set('fiscalYearStartMonth', Number(event.target.value))
                          }
                        >
                          {MONTHS.map((month) => (
                            <MenuItem key={month} value={month}>
                              {monthName(month)}
                            </MenuItem>
                          ))}
                        </Select>
                        {fieldErrors.fiscalYearStartMonth !== undefined && (
                          <FormHelperText>{fieldErrors.fiscalYearStartMonth}</FormHelperText>
                        )}
                      </FormControl>
                    )}
                    {form.periodKind === 'ROLLING_MONTHS' && (
                      <TextField
                        label={t('envelope.rollingMonths')}
                        type="number"
                        value={form.rollingMonths}
                        onChange={(event) => set('rollingMonths', event.target.value)}
                        error={fieldErrors.rollingMonths !== undefined}
                        helperText={fieldErrors.rollingMonths}
                        slotProps={{ htmlInput: { min: ROLLING_MIN, max: ROLLING_MAX, step: 1 } }}
                        sx={{ maxWidth: 160 }}
                      />
                    )}
                  </Stack>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {subscribeRequired ? (
            <DialogDoneButton onClick={guard.requestClose} disabled={submitting} />
          ) : (
            <Button onClick={guard.requestClose} disabled={submitting}>
              {t('common.cancel')}
            </Button>
          )}
          {!subscribeRequired && (
            <SubmitButton type="submit" disabled={trimmedName.length === 0} loading={submitting}>
              {editing ? t('common.save') : t('virtualAccounts.create')}
            </SubmitButton>
          )}
        </DialogActions>
      </form>
    </GuardedDialog>
  );
}

/** A type card (v2 L1972): a 3px top rule and a border, `brandBoundary` with the `selected` fill when chosen. */
function KindCard({
  kind,
  checked,
  onSelect,
}: {
  kind: EnvelopeKind;
  checked: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  return (
    <Box
      component="label"
      data-checked={checked}
      data-kind-card={kind}
      sx={{
        flex: 1,
        minWidth: 0,
        cursor: 'pointer',
        p: '11px 13px',
        border: `1px solid ${checked ? tokens.brandBoundary : tokens.line}`,
        borderTop: `3px solid ${checked ? tokens.brandBoundary : tokens.line}`,
        borderRadius: `${tokens.radius.control}px`,
        bgcolor: tokens.surfaceRaised,
        backgroundImage: checked
          ? `linear-gradient(${tokens.selected}, ${tokens.selected})`
          : 'none',
        color: tokens.text,
        '&:has(input:focus-visible)': {
          outline: `2px solid ${tokens.focusRing}`,
          outlineOffset: 2,
        },
      }}
    >
      <input
        type="radio"
        name="envelope-kind"
        checked={checked}
        onChange={onSelect}
        style={visuallyHidden}
      />
      <Typography
        component="span"
        sx={{
          display: 'block',
          fontSize: 14,
          fontWeight: 500,
          color: checked ? tokens.link : tokens.text,
        }}
      >
        {t(`envelope.kind.${kind}`)}
      </Typography>
      <Typography
        component="span"
        sx={{ display: 'block', fontSize: 11, lineHeight: 1.45, mt: '2px', color: tokens.text2 }}
      >
        {t(`envelope.kindCardNote.${kind}`)}
      </Typography>
    </Box>
  );
}
